/**
 * bs-klaviyo-consent-form.js — opens the Klaviyo Fanclub popup only after the
 * cookie banner is out of the way.
 *
 * Problem this solves: the Klaviyo form used its own time trigger (15s from
 * page load). Shopify's consent banner stays up until the visitor decides, so
 * anyone who hesitated got the discount popup stacked on top of the banner —
 * on mobile that covered the whole viewport and neither could be read.
 *
 * Instead the form is opened from here, keyed to the consent decision:
 *   - banner still open  → wait for `visitorConsentCollected`, then a short delay
 *   - already decided    → open after a short delay (no banner is in the way)
 *
 * Also opens after a *declined* banner: the signup is an email opt-in with its
 * own double opt-in, not a cookie, so cookie consent is not what gates it. Note
 * that Klaviyo's onsite script itself may be consent-gated in the app embed —
 * if it is, `openForm` is a no-op for those visitors and this cannot change it.
 *
 * The Klaviyo form itself keeps a long fallback delay in its own trigger rules,
 * so signups do not die silently if the consent API never loads here.
 *
 * `openForm` bypasses Klaviyo's own display rules, so the "already submitted"
 * and "wait 3 days after close" suppressions are mirrored locally off the
 * `klaviyoForms` events. Without that, subscribers would see the popup again.
 *
 * While the configurator dialog is open the form waits and opens 1.5 s after
 * the dialog closes — opened during it, the form would sit behind the modal.
 * Klaviyo's own triggers (120 s delay, exit intent) bypass that wait, and the
 * flyout does not block the page, so the configurator can also open on top of
 * it. Form and teaser are therefore hidden while the dialog is open (26.10.2026);
 * Klaviyo keeps them open, so they are back as soon as the dialog closes.
 *
 * `?fanclub=1` opens the form right away for links that promise it (e.g. the
 * cart and browse abandonment mails): short delay, and the "closed less than
 * 3 days ago" suppression is skipped. Already-submitted visitors still never
 * see it.
 *
 * Debug output: append `?bs_klaviyo=debug` to the URL.
 */
(function () {
  "use strict";

  var FORM_ID = "Te2awt"; // Klaviyo "Fanclub_Form"
  // Short on purpose (since 23.10.2026): most visitors open the configurator
  // within seconds, and anything scheduled later lands behind it.
  var DELAY_AFTER_CONSENT_MS = 5000; // banner just closed, let the page breathe
  var DELAY_IF_DECIDED_MS = 4000; // returning visitor, no banner was in the way
  var DELAY_AFTER_CONFIGURATOR_MS = 1500; // configurator just closed
  var REOPEN_AFTER_CLOSE_MS = 3 * 24 * 60 * 60 * 1000; // mirrors Klaviyo's 3 days
  var STORAGE_KEY = "bs_klaviyo_fanclub";

  var DEBUG = /[?&]bs_klaviyo=debug/.test(window.location.search);
  var REQUESTED = /[?&]fanclub=1(&|$)/.test(window.location.search);
  var DELAY_IF_REQUESTED_MS = 1500;
  var opened = false;

  // The configurator dialog sits in the top layer; nothing of Klaviyo's can
  // render above it. Hide every Klaviyo flyout/popup and this form's teaser
  // while it is open instead of leaving them half visible behind the backdrop.
  var hideStyle = document.createElement("style");
  hideStyle.textContent =
    'body:has(dialog.bs-modal[open]) [role="dialog"]:has([data-testid="FLYOUT"], [data-testid="POPUP"]),' +
    "body:has(dialog.bs-modal[open]) .kl-teaser-" + FORM_ID +
    " { display: none !important; }";
  document.head.appendChild(hideStyle);

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[bs-klaviyo]");
    console.log.apply(console, args);
  }

  /* --- local mirror of Klaviyo's suppression rules ------------------------ */

  function readState() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {}; // private mode / storage disabled → treat as first visit
    }
  }

  function writeState(patch) {
    try {
      var next = readState();
      for (var key in patch) next[key] = patch[key];
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* nothing we can do, and nothing that should break the page */
    }
  }

  function suppressed() {
    var state = readState();
    if (state.submitted) return "already submitted";
    if (REQUESTED) return null; // link asked for the form, ignore the close cooldown
    if (state.closedAt && Date.now() - state.closedAt < REOPEN_AFTER_CLOSE_MS) {
      return "closed less than 3 days ago";
    }
    return null;
  }

  window.addEventListener("klaviyoForms", function (event) {
    if (!event.detail || event.detail.formId !== FORM_ID) return;
    if (event.detail.type === "submit") writeState({ submitted: true });
    if (event.detail.type === "close") writeState({ closedAt: Date.now() });
  });

  /* --- opening ----------------------------------------------------------- */

  // The configurator is a native modal <dialog> (top layer): a form opened
  // while it is up renders behind it, unseen, and the one chance per visit is
  // gone. Hold the form until the dialog closes instead.
  function openConfigurator() {
    return document.querySelector("dialog.bs-modal[open]");
  }

  function openForm() {
    if (opened) return;
    var reason = suppressed();
    if (reason) {
      log("not opening:", reason);
      return;
    }
    var dialog = openConfigurator();
    if (dialog) {
      log("configurator is open, waiting for it to close");
      dialog.addEventListener(
        "close",
        function () {
          window.setTimeout(openForm, DELAY_AFTER_CONFIGURATOR_MS);
        },
        { once: true }
      );
      return;
    }
    opened = true;
    log("opening form");
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(["openForm", FORM_ID]);
  }

  function openFormAfter(ms) {
    if (suppressed()) return; // save the timer when we already know the answer
    if (REQUESTED) ms = DELAY_IF_REQUESTED_MS;
    log("scheduled in", ms, "ms");
    window.setTimeout(openForm, ms);
  }

  /* --- consent ----------------------------------------------------------- */

  // Fires whenever the visitor answers the banner, accept or decline alike —
  // either way the banner is gone and the screen is free.
  document.addEventListener("visitorConsentCollected", function (event) {
    log("consent collected, marketing:", event.detail && event.detail.marketingAllowed);
    openFormAfter(DELAY_AFTER_CONSENT_MS);
  });

  // Returning visitors never see the banner, so no event fires for them —
  // ask the API for the decision they made earlier.
  function checkExistingConsent() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) {
      log("consent API unavailable, leaving it to Klaviyo's fallback delay");
      if (REQUESTED) openFormAfter(DELAY_IF_REQUESTED_MS);
      return;
    }
    try {
      if (privacy.shouldShowBanner()) {
        log("banner is up, waiting for the decision");
        return;
      }
      log("decision already on file, nothing blocking the screen");
      openFormAfter(DELAY_IF_DECIDED_MS);
    } catch (e) {
      log("consent API threw", e);
    }
  }

  if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (error) {
        if (error) {
          log("could not load consent-tracking-api", error);
          return;
        }
        checkExistingConsent();
      }
    );
  } else {
    log("Shopify.loadFeatures missing, nothing to key off");
  }
})();
